import React from 'react';
import SEO from '../../components/common/SEO';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import './Speaker.css';

const Speaker = () => {
  const offerings = [
    {
      title: 'Guided Meditations',
      description: 'Tailored meditation experiences that guide participants into deep states of relaxation and self-awareness.',
      icon: '🧘'
    },
    {
      title: 'Breathwork Sessions',
      description: 'Transformative breathing techniques to release tension, increase energy, and promote emotional healing.',
      icon: '🌬️'
    },
    {
      title: 'Calming Techniques',
      description: 'Practical tools and methods to reduce stress and cultivate inner peace in daily life.',
      icon: '🌿'
    },
    {
      title: 'Energy Healing',
      description: 'Channelling healing energy to balance, restore, and revitalise participants on all levels.',
      icon: '✨'
    },
    {
      title: 'Sound Baths',
      description: 'Immersive sonic experiences using singing bowls, chimes, and other instruments to promote deep healing.',
      icon: '🔔'
    },
    {
      title: 'Mindfulness Workshops',
      description: 'Interactive sessions teaching practical mindfulness techniques for everyday integration.',
      icon: '🌸'
    }
  ];

  const eventTypes = [
    'Corporate Wellness Days',
    'Retreats & Getaways',
    'Women\'s Circles',
    'Team Building Events',
    'Conference Sessions',
    'Private Group Sessions',
    'Wellness Festivals',
    'Yoga Studio Events'
  ];

  return (
    <>
      <SEO
        title="Wellness Speaker & Event Facilitator Sydney | Kirstie Warburton"
        description="Invite Kirstie Warburton to elevate your next event with transformative meditations, breathwork, energy healing and sound baths. Experienced wellness speaker based in Sydney, available for corporate events and retreats."
        url="/speaker"
        keywords="wellness speaker sydney, meditation facilitator, corporate wellness sydney, breathwork events, sound bath facilitator sydney, transformational speaker, corporate event speaker, wellness retreat facilitator, mindfulness workshop facilitator"
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Speaker', path: '/speaker' }]}
      />

      <div className="speaker-page">
        <section className="speaker-hero">
          <div className="container">
            <div className="speaker-hero__badge">Soulful Transformational Leader</div>
            <h1>Elevate Your Event with <span className="text-gradient">Heart-Centred Transformation</span></h1>
            <p className="speaker-hero__lead">
              Kirstie is devoted to bringing heartfelt guidance, inspiration, and holistic wellness
              to your next event. Through a harmonious blend of transformative practices, she creates
              immersive experiences that nourish the mind, body, and spirit.
            </p>
            <div className="speaker-hero__cta">
              <Button href="/contact" size="large" variant="accent">
                Enquire About Booking
              </Button>
            </div>
          </div>
        </section>

        <section className="section speaker-intro">
          <div className="container">
            <div className="speaker-intro__content">
              <div className="speaker-intro__image">
                <img
                  src="https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800&q=80"
                  alt="Kirstie facilitating a wellness session"
                />
              </div>
              <div className="speaker-intro__text">
                <h2>Creating Unforgettable Experiences</h2>
                <p>
                  Through a harmonious blend of transformative practices—including meditations, breathwork,
                  calming techniques, energy healing, and soothing sound baths—Kirstie creates immersive
                  experiences that nourish the mind, body, and spirit.
                </p>
                <p>
                  Her sessions are thoughtfully designed to foster inner peace, self-awareness, and a deep
                  connection to one's true essence. Participants leave feeling revitalised, centred, and
                  empowered with practical tools to integrate mindfulness and well-being into their daily lives.
                </p>
                <p className="speaker-intro__highlight">
                  If you are seeking a compassionate and transformative speaker capable of elevating your
                  event with enriching content and practical wisdom, Kirstie is the ideal choice. Inspire
                  your audience and allow Kirstie to cultivate an unforgettable experience that promotes
                  holistic health and profound personal growth.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="section speaker-offerings bg-light-beige">
          <div className="container">
            <h2 className="text-center">What Kirstie Offers</h2>
            <p className="section-subtitle text-center">
              A curated selection of transformative practices tailored to your event's needs
            </p>
            <div className="offerings-grid">
              {offerings.map((offering, index) => (
                <Card key={index} className="offering-card">
                  <div className="offering-icon">{offering.icon}</div>
                  <h3>{offering.title}</h3>
                  <p>{offering.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="section speaker-events">
          <div className="container">
            <div className="speaker-events__content">
              <div className="speaker-events__text">
                <h2>Perfect For Any Occasion</h2>
                <p>
                  Whether you're organising a corporate wellness initiative, a private retreat,
                  or a community gathering, Kirstie adapts her offerings to create the perfect
                  experience for your audience.
                </p>
                <ul className="event-types-list">
                  {eventTypes.map((type, index) => (
                    <li key={index}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      {type}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="speaker-events__image">
                <img
                  src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&q=80"
                  alt="Group meditation session"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="section speaker-benefits bg-beige">
          <div className="container">
            <h2 className="text-center">What Participants Experience</h2>
            <div className="benefits-grid">
              <div className="benefit-item">
                <div className="benefit-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
                <h3>Inner Peace</h3>
                <p>Cultivate a deep sense of calm and tranquility that extends beyond the session.</p>
              </div>
              <div className="benefit-item">
                <div className="benefit-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 6v6l4 2"/>
                  </svg>
                </div>
                <h3>Self-Awareness</h3>
                <p>Discover deeper insights into your true self and what brings you joy.</p>
              </div>
              <div className="benefit-item">
                <div className="benefit-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                </div>
                <h3>Connection</h3>
                <p>Foster meaningful connections with yourself and others in a supportive space.</p>
              </div>
              <div className="benefit-item">
                <div className="benefit-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 8h1a4 4 0 0 1 0 8h-1"/>
                    <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/>
                    <line x1="6" y1="1" x2="6" y2="4"/>
                    <line x1="10" y1="1" x2="10" y2="4"/>
                    <line x1="14" y1="1" x2="14" y2="4"/>
                  </svg>
                </div>
                <h3>Practical Tools</h3>
                <p>Leave with actionable techniques to integrate mindfulness into daily life.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="section speaker-cta">
          <div className="container">
            <div className="speaker-cta__content text-center">
              <h2>Ready to Transform Your Event?</h2>
              <p>
                Let's discuss how Kirstie can create a bespoke experience that aligns with your
                vision and leaves a lasting impact on your attendees.
              </p>
              <div className="speaker-cta__buttons">
                <Button href="/contact" size="large" variant="accent">
                  Get in Touch
                </Button>
                <Button href="/about" size="large" variant="secondary">
                  Learn More About Kirstie
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Speaker;
